import type { PositionWithReadiness, TreeNode } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { childrenOf, typeOf, type TreeIndex } from '../app/node-tree-path.js';
import { BigCards } from './BigCards.js';
import { PositionHeatmap } from './PositionHeatmap.js';

/**
 * 部门站 / 岗位站的三块（原型 v0.22）：
 *
 * | 块 | 原型出处 |
 * |---|---|
 * | 五个指标条 `kchips` | 1176–1188 行 |
 * | 时间热力图 `hourRows` | 1118–1127 行 |
 * | 两个视角 `perspHTML` | 1129–1133 行 |
 *
 * ## 五个指标条：三格真、两格「待编制数据到位」
 *
 * **这两格不给演示值**，与首屏五数同一条裁定（C-099R 三）：
 *
 * - 「岗位 / 人数 / 任务卡」有源，照实显示
 * - 「AI 替代率」「日总工时 · AI 完成」**无源**——它们不是 0，是**算不出来**。
 *   给个演示百分比会被读成「AI 已经替代了 86%」，那是**会误导决策的假值**（P-13、P-15）
 *
 * C-159 二说「没有的格子用原型演示值并标 SBX」，而 C-099R 三对**这两个数**
 * 已有更具体的裁定。**具体的那条优先**——同一个数在首屏标「待编制数据到位」、
 * 在部门站给个演示值，看的人会以为部门站算出来了。
 *
 * ## 人数取的是**问卷人数**，不是编制
 *
 * 库里 `position_headcount`（逐岗编制）为空，`department.headcount_surveyed`
 * （问卷人数）是**真数据**。故这一格显示问卷人数并**写明它是问卷值**——
 * C-143R 裁定一已把部门人数降为导入偏差，不进确认机制，但它仍然是客户给的真数字。
 *
 * ## 时间热力图：**零态**，不再画演示格（C-160 口径一）
 *
 * 它要两样：**逐岗编制**（`position_headcount`，表在、零行 → 零态）
 * 与**每小时由谁完成**（`taskrun`，表在、零行 → 零态）。
 *
 * 此前这里按编码派生演示小时数与绿灰比例。那是**用演示值顶替零态**，
 * 而口径一 明确禁止两态互相顶替。故现在：
 *
 * - 编制录了的岗位：按真人数画格子，**格子一律零态灰**（没有一小时有执行记录）
 * - 编制没录的岗位：**不画格子**，写明「编制未录」——不编一个人数出来
 */

const HOURS_PER_DAY = 8;

export function StationOverview({
  index,
  node,
  onSelect,
}: {
  index: TreeIndex;
  /** 站在哪个节点上。null 是企业站——企业站有首屏五数，不用这一块。 */
  node: TreeNode;
  onSelect: (id: string) => void;
}): ReactNode {
  const [positions, setPositions] = useState<PositionWithReadiness[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    /*
     * 编制不在树投影里（树只带 memberCount＝挂在节点上的人），所以单独拉一次
     * 岗位清单——**不在前端现算 sum**：`v_department_headcount_current`
     * 是编制这个数的唯一定义处（store.ts:114 已写明这条）。
     *
     * 部门清单（问卷人数那一侧）这里**不拉**：拉了没人读，就是一次白跑的请求。
     * 它随首屏第二格那一格的收口一起回来。
     */
    let live = true;
    void api.positions().then(
      (p) => {
        if (!live) return;
        setPositions(p.rows);
      },
      (cause: unknown) => live && setError(messageOf(cause)),
    );
    return () => {
      live = false;
    };
  }, []);

  const isDept = typeOf(index, node)?.code === 'DEPARTMENT';

  /** 这一站下面的岗位（树上的真数据）。 */
  const posNodes = isDept
    ? childrenOf(index, node.id).filter((n) => typeOf(index, n)?.code === 'POSITION')
    : [node];

  /**
   * 在岗人数：**读树属性里的 `staffedCount`**（库算），前端不数人。
   *
   * 「在岗」的判定是「任职区间是否覆盖当下」——那个判定只该有一处
   * （`v_position_staffed_count`）。前端拿到的是数，不是规则。
   */
  const staffed = posNodes.reduce((sum, p) => {
    const n = p.attributes['staffedCount'];
    return sum + (typeof n === 'number' ? n : 0);
  }, 0);

  const atcs = posNodes.flatMap((p) =>
    childrenOf(index, p.id).filter((n) => typeOf(index, n)?.kind === 'ASSET'),
  );

  /** 逐岗编制（真数据，可能全为 null）。 */
  const planned = posNodes.map((p) => {
    const row = positions?.find((r) => r.id === p.id);
    return row?.headcount ?? null;
  });

  if (error !== null) return <p className="msg">{error}</p>;

  return (
    <div className="pv22 pv22-stack">
      <div className="kchips">
        {/*
          **一格两数**，与企业级首屏同法（Michael 裁定）：
          岗位数为主、在岗人数为辅。岗位站上岗位数恒为 1，没有信息量，只显人数。

          人数取的是**在岗人数**（人员表的任职区间数出来的统计值），
          不是编制、也不是问卷值——那两个是别的问题的答案：
          编制是「该有几个人」，问卷是「客户说有几个人」，在岗是「现在有几个人」。
        */}
        <div className="kchip c1">
          <div className="l">{isDept ? '岗位' : '在岗人数'}</div>
          {isDept ? (
            <>
              <div className="v">
                {posNodes.length}
                <small>个</small>
              </div>
              <div className="note">在岗 {staffed} 人</div>
            </>
          ) : (
            <div className="v">
              {staffed}
              <small>人</small>
            </div>
          )}
        </div>

        <div className="kchip c3">
          <div className="l">任务卡（已建模 / 规划）</div>
          <div className="v">
            {atcs.filter((a) => a.enabled).length}
            <small>/ {atcs.length} 张</small>
          </div>
        </div>

        {/*
          两格无源。**不显示数字位**——不是 0、不是 `--` 占位，那两种都会被
          眼睛读成「有这个数，值是零／很小」。与企业级首屏的 `Blocked` 同形，
          **文案也与它一致**：同一个产品里不留两种说法。
        */}
        <div className="kchip c4">
          <div className="l">AI 替代率</div>
          <div className="note">待编制数据到位</div>
        </div>
        <div className="kchip c5">
          <div className="l">日总工时 / AI 完成</div>
          <div className="note">待编制数据到位</div>
        </div>
      </div>

      {/*
        块的顺序**照原型 `depHTML`（1206–1223 行）**：
        指标条 → 任务卡平面图 → 岗位 ATC 热力图 → 时间热力图 → 两个视角。

        上一版的顺序是「指标条 → 时间热力图 → 视角 → 平面图 → 热力图」，
        并排对照图（C-160 A6）一眼看出不对。**顺序也是结构**——
        T① 的判据是逐屏一致，不是「该有的块都有」。
      */}
      <BigCards
        groups={posNodes.map((p, i) => ({
          position: p,
          headcount: planned[i] ?? null,
          atcs: childrenOf(index, p.id).filter((n) => typeOf(index, n)?.kind === 'ASSET'),
        }))}
        onPick={onSelect}
        onEnterPosition={onSelect}
      />

      <PositionHeatmap index={index} positions={posNodes} onPickAtc={onSelect} />

      <div className="card">
        <h3>
          时间热力图{' '}
          <span className="sub">
            逐小时看这一天的工作由谁完成 · 绿＝AI，灰＝人工 · 一行＝一个岗位
          </span>
        </h3>

        {posNodes.length === 0 ? (
          <div className="note">这个部门下还没有岗位，没有行可画。</div>
        ) : (
          posNodes.map((p, i) => {
            /*
             * `planned` 与 `posNodes` 同长同序；`?? null` 把「越界」与「没录编制」
             * 并成同一种——两者在界面上要显示的是同一句。
             */
            const real = planned[i] ?? null;
            return (
              <div className="hrow" key={p.id}>
                <span className="pn" title={p.name}>
                  {p.name} · {real !== null ? `${real} 人` : '编制未录'}
                </span>
                <span className="hours">
                  {real === null ? (
                    /*
                     * 没有编制就没有「一天几个小时」。**不编一个人数出来**画格子。
                     *
                     * 这一处上一轮改过一次而**没改成**：我替换的是 map 内部那段，
                     * 而那段的前一行早先插过一句注释，字符串没对上、`replace` 静默没生效。
                     * 于是脚注写着「没有演示值」，代码里还在按编码派生小时数与绿灰比例。
                     * **注释与文案都是未经核实的断言**——这一条又中了一次。
                     */
                    <span className="note">编制未录，画不出小时格</span>
                  ) : (
                    Array.from({ length: real * HOURS_PER_DAY }, (_, h) => (
                      <span
                        className="hb hu"
                        key={h}
                        title={`${p.name} · 第 ${h + 1} 小时 · 还没有执行记录`}
                      />
                    ))
                  )}
                </span>
              </div>
            );
          })
        )}

        <div className="note" style={{ marginTop: 8 }}>
          编制录了的岗位按真人数画格（行首写着几人），没录的不画。
          格子现在一律是灰的：这一天还没有任何一次执行记录。
          第一次执行发生后，跑过的那些小时会变成绿的。
        </div>
      </div>

      {/*
        原型这里还有「两个视角」两段说明（时间视角 / 任务视角 · 全量映射）。
        **整段不搬**：那是概念解释，不是这一屏的数据。

        判别一问：这两段去掉之后，屏幕上的东西还看不看得懂？看得懂——
        指标条、平面图、时间热力图各自的标题已经说明了怎么读。
      */}
    </div>
  );
}
